import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getSettings, invalidateSettingsCache } from '../utils/settingsCache';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Camera, Mail, Phone } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import { useTheme, useSetTheme } from '../context/ThemeContext';
import THEMES, { resolveTheme } from '../constants/themes';
import SOCIAL_PLATFORMS from '../constants/socialIcons';
import LoginsAccess from '../components/LoginsAccess';
import { useBandRole, useCanEdit } from '../context/BandContext';

const API_URL = process.env.REACT_APP_API_URL || '';

const empty = {
  band_name: '', tagline: '', band_slug: '', logo_url: '', promo_photo_url: '',
  promo_photo_x: 50, promo_photo_y: 50, promo_photo_zoom: 100,
  splash_bg_url: '', splash_bg_x: 50, splash_bg_y: 50, splash_bg_zoom: 100,
  header_bg_x: 50, header_bg_y: 50,
  theme_name: 'default',
  primary_color: '#667eea', secondary_color: '#764ba2',
  background_color: '#ffffff', text_color: '#333333',
  facebook_url: '', instagram_url: '', twitter_url: '', youtube_url: '',
  tiktok_url: '', spotify_url: '', apple_music_url: '', soundcloud_url: '',
  website_url: '', show_social_media: true, welcome_message: '', footer_text: '',
  booking_email: '', booking_phone: '', show_booking_info: true,
  auto_activate_enabled: false, auto_activate_mins_before: 120, auto_deactivate_mins_after: 120,
  public_page_layout: 'hero',
  tech_rider_full: null, tech_rider_minimal: null,
};

const emptyRider = (title) => ({
  title,
  inputs: [],
  pa_required: false,
  monitors: '',
  backline: '',
  notes: '',
});

function getCroppedImg(imgEl, pixelCrop) {
  const canvas = document.createElement('canvas');
  const size = 300;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const scaleX = imgEl.naturalWidth / imgEl.width;
  const scaleY = imgEl.naturalHeight / imgEl.height;
  ctx.drawImage(
    imgEl,
    pixelCrop.x * scaleX, pixelCrop.y * scaleY,
    pixelCrop.width * scaleX, pixelCrop.height * scaleY,
    0, 0, size, size,
  );
  return canvas.toDataURL('image/jpeg', 0.85);
}

function BrandingSettings() {
  const theme = useTheme();
  const setTheme = useSetTheme();
  const [settings, setSettings] = useState(empty);
  const { bandRole } = useBandRole();
  const canEdit = useCanEdit();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('band');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingPromo, setUploadingPromo] = useState(false);
  const logoInputRef = useRef(null);
  const promoInputRef = useRef(null);
  const promoPreviewRef = useRef(null);
  const promoDragStart = useRef(null);
  const [promoDragging, setPromoDragging] = useState(false);
  const splashPreviewRef = useRef(null);
  const splashDragStart = useRef(null);
  const [splashDragging, setSplashDragging] = useState(false);
  const headerPreviewRef = useRef(null);
  const headerDragStart = useRef(null);
  const [headerDragging, setHeaderDragging] = useState(false);
  const [uploadingSplash, setUploadingSplash] = useState(false);
  const splashInputRef = useRef(null);

  const [activeRiderProfile, setActiveRiderProfile] = useState('full');
  const [savingRider, setSavingRider] = useState(false);

  const emptyMember = { name: '', role: '', bio: '', photo_url: '' };
  const [members, setMembers] = useState([]);
  const [memberForm, setMemberForm] = useState(emptyMember);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [savingMember, setSavingMember] = useState(false);

  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = useRef(null);
  const memberPhotoInputRef = useRef(null);

  useEffect(() => {
    getSettings()
      .then(data => {
        if (data && data.id) {
          setSettings(s => ({ ...s, ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v ?? ''])) }));
        }
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const fetchMembers = () => {
    axios.get(`${API_URL}/api/band-members`)
      .then(res => setMembers(res.data))
      .catch(() => toast.error('Failed to load band members'));
  };

  useEffect(() => { fetchMembers(); }, []);

  const saveMember = async () => {
    if (!memberForm.name.trim()) { toast.error('Name is required'); return; }
    setSavingMember(true);
    try {
      if (editingMemberId) {
        await axios.put(`${API_URL}/api/band-members/${editingMemberId}`, memberForm);
        toast.success('Member updated');
      } else {
        await axios.post(`${API_URL}/api/band-members`, memberForm);
        toast.success('Member added');
      }
      setMemberForm(emptyMember);
      setEditingMemberId(null);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save member');
    } finally {
      setSavingMember(false);
    }
  };

  const deleteMember = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/band-members/${id}`);
      toast.success('Member removed');
      fetchMembers();
    } catch {
      toast.error('Failed to remove member');
    }
  };

  const startEditMember = (m) => {
    setMemberForm({ name: m.name, role: m.role || '', bio: m.bio || '', photo_url: m.photo_url || '' });
    setEditingMemberId(m.id);
  };

  const onSelectMemberPhoto = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    if (file.size > 8 * 1024 * 1024) { toast.error('Photo must be under 8MB'); return; }
    setCropSrc(URL.createObjectURL(file));
    setCrop(undefined);
    setCompletedCrop(undefined);
    e.target.value = '';
  };

  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 85 }, 1, width, height), width, height));
  };

  const confirmCrop = () => {
    if (!completedCrop || !imgRef.current) return;
    const dataUrl = getCroppedImg(imgRef.current, completedCrop);
    setMemberForm(f => ({ ...f, photo_url: dataUrl }));
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const cancelCrop = () => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleChange = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const applyTheme = (theme) => {
    setSettings(s => ({
      ...s,
      theme_name: theme.id,
      ...(theme.id !== 'custom' ? {
        primary_color: theme.primary,
        secondary_color: theme.secondary,
        background_color: theme.background,
        text_color: theme.text,
      } : {}),
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('logo', file);
      const res = await axios.post(`${API_URL}/api/upload/logo`, form);
      setSettings(s => ({ ...s, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    try {
      await axios.delete(`${API_URL}/api/settings/logo`);
      setSettings(s => ({ ...s, logo_url: '' }));
      toast.success('Logo removed');
    } catch {
      toast.error('Failed to remove logo');
    }
  };

  const handlePromoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo must be under 10MB'); return; }
    setUploadingPromo(true);
    try {
      const form = new FormData();
      form.append('photo', file);
      const res = await axios.post(`${API_URL}/api/upload/promo`, form);
      setSettings(s => ({ ...s, promo_photo_url: res.data.promo_photo_url }));
      toast.success('Promo photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Promo photo upload failed');
    } finally {
      setUploadingPromo(false);
    }
  };

  const removePromo = async () => {
    try {
      await axios.delete(`${API_URL}/api/settings/promo`);
      setSettings(s => ({ ...s, promo_photo_url: '', promo_photo_x: 50, promo_photo_y: 50, promo_photo_zoom: 100 }));
      toast.success('Promo photo removed');
    } catch {
      toast.error('Failed to remove promo photo');
    }
  };

  const handlePromoDragStart = (e) => {
    e.preventDefault();
    const el = promoPreviewRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    promoDragStart.current = {
      startX: e.clientX,
      startY: e.clientY,
      bgX: settings.promo_photo_x ?? 50,
      bgY: settings.promo_photo_y ?? 50,
    };
    setPromoDragging(true);

    const onMove = (ev) => {
      if (!promoDragStart.current) return;
      const rect = el.getBoundingClientRect();
      const dx = ev.clientX - promoDragStart.current.startX;
      const dy = ev.clientY - promoDragStart.current.startY;
      const sensitivity = 100 / rect.width;
      const newX = Math.max(0, Math.min(100, promoDragStart.current.bgX - dx * sensitivity));
      const newY = Math.max(0, Math.min(100, promoDragStart.current.bgY - dy * sensitivity));
      setSettings(s => ({ ...s, promo_photo_x: newX, promo_photo_y: newY }));
    };

    const onUp = () => {
      setPromoDragging(false);
      promoDragStart.current = null;
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  };

  const handleSplashUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setUploadingSplash(true);
    try {
      const form = new FormData();
      form.append('photo', file);
      const res = await axios.post(`${API_URL}/api/upload/splash`, form);
      setSettings(s => ({ ...s, splash_bg_url: res.data.splash_bg_url }));
      toast.success('Splash background uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingSplash(false);
    }
  };

  const removeSplash = async () => {
    try {
      await axios.delete(`${API_URL}/api/settings/splash`);
      setSettings(s => ({ ...s, splash_bg_url: '', splash_bg_x: 50, splash_bg_y: 50, splash_bg_zoom: 100 }));
      toast.success('Splash background removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleSplashDragStart = (e) => {
    e.preventDefault();
    const el = splashPreviewRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    splashDragStart.current = {
      startX: e.clientX,
      startY: e.clientY,
      bgX: settings.splash_bg_x ?? 50,
      bgY: settings.splash_bg_y ?? 50,
    };
    setSplashDragging(true);

    const onMove = (ev) => {
      if (!splashDragStart.current) return;
      const rect = el.getBoundingClientRect();
      const dx = ev.clientX - splashDragStart.current.startX;
      const dy = ev.clientY - splashDragStart.current.startY;
      const sensitivity = 100 / rect.width;
      const newX = Math.max(0, Math.min(100, splashDragStart.current.bgX - dx * sensitivity));
      const newY = Math.max(0, Math.min(100, splashDragStart.current.bgY - dy * sensitivity));
      setSettings(s => ({ ...s, splash_bg_x: newX, splash_bg_y: newY }));
    };

    const onUp = () => {
      setSplashDragging(false);
      splashDragStart.current = null;
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  };

  const handleHeaderDragStart = (e) => {
    e.preventDefault();
    const el = headerPreviewRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    headerDragStart.current = {
      startX: e.clientX,
      startY: e.clientY,
      bgX: settings.header_bg_x ?? 50,
      bgY: settings.header_bg_y ?? 50,
    };
    setHeaderDragging(true);

    const onMove = (ev) => {
      if (!headerDragStart.current) return;
      const rect = el.getBoundingClientRect();
      const dx = ev.clientX - headerDragStart.current.startX;
      const dy = ev.clientY - headerDragStart.current.startY;
      const sensitivity = 100 / rect.width;
      const newX = Math.max(0, Math.min(100, headerDragStart.current.bgX - dx * sensitivity));
      const newY = Math.max(0, Math.min(100, headerDragStart.current.bgY - dy * sensitivity));
      setSettings(s => ({ ...s, header_bg_x: newX, header_bg_y: newY }));
    };

    const onUp = () => {
      setHeaderDragging(false);
      headerDragStart.current = null;
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { logo_url, promo_photo_url, splash_bg_url, ...payload } = settings;
      await axios.put(`${API_URL}/api/settings`, payload);
      invalidateSettingsCache();
      setTheme(resolveTheme(settings));
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const tabStyle = (id) => ({
    padding: '0.45rem 1rem',
    border: activeTab === id ? 'none' : `1px solid ${theme.border}`,
    borderRadius: '20px',
    cursor: 'pointer',
    background: activeTab === id ? theme.primary : 'transparent',
    fontWeight: activeTab === id ? '600' : '400',
    color: activeTab === id ? theme.onPrimary : theme.textSecondary,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'background 0.15s, color 0.15s',
  });

  const inputStyle = {
    width: '100%', padding: '0.65rem 0.75rem', border: `1px solid ${theme.border}`,
    borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box',
    background: theme.surface, color: theme.text,
  };

  const labelStyle = {
    display: 'block', fontWeight: '600', fontSize: '0.85rem',
    color: theme.text, marginBottom: '0.4rem',
  };

  const getRider = (profile) => {
    const key = profile === 'full' ? 'tech_rider_full' : 'tech_rider_minimal';
    return settings[key] || emptyRider(profile === 'full' ? 'Full Setup' : 'Minimal Setup');
  };

  const setRider = (profile, updates) => {
    const key = profile === 'full' ? 'tech_rider_full' : 'tech_rider_minimal';
    setSettings(s => ({ ...s, [key]: { ...getRider(profile), ...updates } }));
  };

  const updateRiderInput = (profile, idx, field, value) => {
    const rider = getRider(profile);
    const inputs = rider.inputs.map((row, i) => i === idx ? { ...row, [field]: value } : row);
    setRider(profile, { inputs });
  };

  const addRiderInput = (profile) => {
    const rider = getRider(profile);
    const nextCh = rider.inputs.length > 0 ? Math.max(...rider.inputs.map(r => Number(r.ch) || 0)) + 1 : 1;
    setRider(profile, { inputs: [...rider.inputs, { ch: nextCh, name: '', source: 'mic' }] });
  };

  const removeRiderInput = (profile, idx) => {
    const rider = getRider(profile);
    setRider(profile, { inputs: rider.inputs.filter((_, i) => i !== idx) });
  };

  const saveRider = async (profile) => {
    setSavingRider(true);
    try {
      await axios.put(`${API_URL}/api/tech-rider/${profile}`, getRider(profile));
      toast.success('Tech rider saved');
    } catch {
      toast.error('Failed to save tech rider');
    } finally {
      setSavingRider(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!canEdit) {
    return (
      <p style={{ padding: '2rem', color: theme.textMuted }}>
        Branding & Settings can only be changed by the band's owner or admins.
      </p>
    );
  }

  return (
    <div style={{ background: theme.background }}>
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem', color: theme.text }}>

      {/* Tabs */}
      <style>{`.gr-tabs::-webkit-scrollbar{display:none}`}</style>
      <div className="gr-tabs" style={{
        overflowX: 'auto', marginBottom: '2rem',
        msOverflowStyle: 'none', scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', width: 'max-content', paddingBottom: '2px' }}>
          <button style={tabStyle('band')} onClick={() => setActiveTab('band')}>Band Info</button>
          <button style={tabStyle('theme')} onClick={() => setActiveTab('theme')}>Theme</button>
          <button style={tabStyle('social')} onClick={() => setActiveTab('social')}>Social Links</button>
          <button style={tabStyle('members')} onClick={() => setActiveTab('members')}>Band Members</button>
          <button style={tabStyle('automation')} onClick={() => setActiveTab('automation')}>Automation</button>
          <button style={tabStyle('publicpage')} onClick={() => setActiveTab('publicpage')}>Public Page</button>
          <button style={tabStyle('techrider')} onClick={() => setActiveTab('techrider')}>Tech Rider</button>
          {bandRole === 'owner' && (
            <button style={tabStyle('logins')} onClick={() => setActiveTab('logins')}>Logins &amp; Access</button>
          )}
        </div>
      </div>

      {/* ── Band Info tab ── */}
      {activeTab === 'band' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          <div>
            <label style={labelStyle}>Band Name</label>
            <input style={inputStyle} value={settings.band_name} onChange={e => handleChange('band_name', e.target.value)} placeholder="e.g. The Rusty Nails" />
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>Displayed on the audience landing page and welcome modal</p>
          </div>

          <div>
            <label style={labelStyle}>Audience URL Slug</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ color: theme.textMuted, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{window.location.host}/gig/</span>
              <input
                style={inputStyle}
                value={settings.band_slug}
                onChange={e => handleChange('band_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))}
                placeholder="e.g. rusty-nails"
              />
            </div>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>Permanent shareable link for your audience — never changes between gigs</p>
          </div>

          <div>
            <label style={labelStyle}>Tagline</label>
            <input style={inputStyle} value={settings.tagline} onChange={e => handleChange('tagline', e.target.value)} placeholder="e.g. Classic Rock Covers" />
          </div>

          <div>
            <label style={labelStyle}>Logo</label>
            {settings.logo_url ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <img src={settings.logo_url} alt="Band logo" style={{ width: '80px', height: '80px', objectFit: 'contain', border: `1px solid ${theme.border}`, borderRadius: '8px', background: theme.surfaceAlt, padding: '4px' }} />
                <div>
                  <button onClick={() => logoInputRef.current.click()} style={{ display: 'block', marginBottom: '0.4rem', padding: '0.4rem 0.9rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                    Replace
                  </button>
                  <button onClick={removeLogo} style={{ display: 'block', padding: '0.4rem 0.9rem', background: theme.surface, color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => logoInputRef.current.click()}
                style={{ border: `2px dashed ${theme.border}`, borderRadius: '10px', padding: '2rem', textAlign: 'center', cursor: 'pointer', color: theme.textMuted, background: theme.surfaceAlt }}
              >
                {uploadingLogo ? 'Uploading…' : <><Camera size={16} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />Click to upload logo<br /><span style={{ fontSize: '0.8rem' }}>PNG, JPG or SVG · max 2MB</span></>}
              </div>
            )}
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>Replaces the music icon in the audience welcome modal</p>
          </div>

          <div>
            <label style={labelStyle}>Promo Photo</label>
            {settings.promo_photo_url ? (
              <div>
                {/* Interactive crop/zoom preview */}
                <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
                  <div
                    ref={promoPreviewRef}
                    onPointerDown={handlePromoDragStart}
                    style={{
                      width: '100%', height: '140px',
                      overflow: 'hidden',
                      borderRadius: '8px',
                      border: `1px solid ${theme.border}`,
                      cursor: promoDragging ? 'grabbing' : 'grab',
                      userSelect: 'none',
                      touchAction: 'none',
                      backgroundColor: theme.surfaceAlt,
                      position: 'relative',
                    }}
                  >
                    <img
                      src={settings.promo_photo_url}
                      alt=""
                      draggable={false}
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover',
                        objectPosition: `${settings.promo_photo_x ?? 50}% ${settings.promo_photo_y ?? 50}%`,
                        transform: `scale(${(settings.promo_photo_zoom || 100) / 100})`,
                        transformOrigin: 'center center',
                        display: 'block',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                  <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '0.7rem', borderRadius: '4px', padding: '2px 7px', pointerEvents: 'none' }}>
                    Drag to reposition
                  </div>
                </div>
                {/* Zoom slider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted, flexShrink: 0 }}>Zoom</span>
                  <input
                    type="range" min="100" max="250" step="5"
                    value={settings.promo_photo_zoom || 100}
                    onChange={e => setSettings(s => ({ ...s, promo_photo_zoom: Number(e.target.value) }))}
                    style={{ flex: 1, accentColor: theme.primary, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted, flexShrink: 0, width: '2.8rem', textAlign: 'right' }}>{Math.round(settings.promo_photo_zoom || 100)}%</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => promoInputRef.current.click()} style={{ padding: '0.4rem 0.9rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                    Replace
                  </button>
                  <button onClick={removePromo} style={{ padding: '0.4rem 0.9rem', background: theme.surface, color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => promoInputRef.current.click()}
                style={{ border: `2px dashed ${theme.border}`, borderRadius: '10px', padding: '2rem', textAlign: 'center', cursor: 'pointer', color: theme.textMuted, background: theme.surfaceAlt }}
              >
                {uploadingPromo ? 'Uploading…' : <><Camera size={16} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />Click to upload promo photo<br /><span style={{ fontSize: '0.8rem' }}>JPG or PNG · max 10MB · landscape recommended</span></>}
              </div>
            )}
            <input ref={promoInputRef} type="file" accept="image/*" onChange={handlePromoUpload} style={{ display: 'none' }} />
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>Appears as a banner on the printed gig promo sheet</p>
          </div>

          {settings.promo_photo_url && (
            <div>
              <label style={labelStyle}>Audience Header Crop</label>
              <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
                <div
                  ref={headerPreviewRef}
                  onPointerDown={handleHeaderDragStart}
                  style={{
                    width: '100%', height: '72px',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    border: `1px solid ${theme.border}`,
                    cursor: headerDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    touchAction: 'none',
                    backgroundColor: theme.surfaceAlt,
                    position: 'relative',
                  }}
                >
                  <img
                    src={settings.promo_photo_url}
                    alt=""
                    draggable={false}
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      objectPosition: `${settings.header_bg_x ?? 50}% ${settings.header_bg_y ?? 50}%`,
                      display: 'block',
                      pointerEvents: 'none',
                    }}
                  />
                  <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${settings.primary_color || '#667eea'} 0%, ${settings.secondary_color || '#764ba2'} 100%)`, opacity: 0.75, pointerEvents: 'none' }} />
                </div>
                <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '0.7rem', borderRadius: '4px', padding: '2px 7px', pointerEvents: 'none' }}>
                  Drag to reposition
                </div>
              </div>
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>Sets the focal point for the audience page header strip — independent of the print sheet crop above</p>
            </div>
          )}

          <div>
            <label style={labelStyle}>Splash Screen Background</label>
            {settings.splash_bg_url ? (
              <div>
                <div style={{ position: 'relative', marginBottom: '0.5rem', display: 'flex', justifyContent: 'center' }}>
                  <div style={{ position: 'relative', width: '160px' }}>
                    <div
                      ref={splashPreviewRef}
                      onPointerDown={handleSplashDragStart}
                      style={{
                        width: '160px', height: '284px',
                        overflow: 'hidden',
                        borderRadius: '10px',
                        border: `1px solid ${theme.border}`,
                        cursor: splashDragging ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        touchAction: 'none',
                        backgroundColor: theme.surfaceAlt,
                        position: 'relative',
                      }}
                    >
                      <img
                        src={settings.splash_bg_url}
                        alt=""
                        draggable={false}
                        style={{
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                          objectPosition: `${settings.splash_bg_x ?? 50}% ${settings.splash_bg_y ?? 50}%`,
                          transform: `scale(${(settings.splash_bg_zoom || 100) / 100})`,
                          transformOrigin: 'center center',
                          display: 'block',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>
                    <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '0.7rem', borderRadius: '4px', padding: '2px 7px', pointerEvents: 'none' }}>
                      Drag to reposition
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted, flexShrink: 0 }}>Zoom</span>
                  <input
                    type="range" min="100" max="300" step="5"
                    value={settings.splash_bg_zoom || 100}
                    onChange={e => setSettings(s => ({ ...s, splash_bg_zoom: Number(e.target.value) }))}
                    style={{ flex: 1, accentColor: theme.primary, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted, flexShrink: 0, width: '2.8rem', textAlign: 'right' }}>{Math.round(settings.splash_bg_zoom || 100)}%</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => splashInputRef.current.click()} style={{ padding: '0.4rem 0.9rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Replace</button>
                  <button onClick={removeSplash} style={{ padding: '0.4rem 0.9rem', background: theme.surface, color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Remove</button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => splashInputRef.current.click()}
                style={{ border: `2px dashed ${theme.border}`, borderRadius: '10px', padding: '2rem', textAlign: 'center', cursor: 'pointer', color: theme.textMuted, background: theme.surfaceAlt }}
              >
                {uploadingSplash ? 'Uploading…' : <><Camera size={16} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} />Click to upload splash background<br /><span style={{ fontSize: '0.8rem' }}>Portrait photo recommended · JPG or PNG · max 10MB</span></>}
              </div>
            )}
            <input ref={splashInputRef} type="file" accept="image/*" onChange={handleSplashUpload} style={{ display: 'none' }} />
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>Full-screen background image on the audience welcome screen</p>
          </div>

          <div>
            <label style={labelStyle}>Welcome Message <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
            <textarea
              value={settings.welcome_message}
              onChange={e => handleChange('welcome_message', e.target.value)}
              placeholder="e.g. Thanks for coming tonight — enjoy the show!"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div>
            <label style={labelStyle}>Footer Text <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
            <input style={inputStyle} value={settings.footer_text} onChange={e => handleChange('footer_text', e.target.value)} placeholder="e.g. Follow us on socials for upcoming gigs!" />
          </div>

          {/* Booking info — used on the printable promo sheet */}
          <div style={{ paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
            <p style={{ margin: '0 0 1rem', fontWeight: '700', fontSize: '0.85rem', color: theme.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Booking Info</p>
            <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: theme.textMuted }}>Shown on the printable gig promo sheet when enabled.</p>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settings.show_booking_info}
                onChange={e => handleChange('show_booking_info', e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>Show booking info on promo sheet</span>
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', opacity: settings.show_booking_info ? 1 : 0.4 }}>
              <div>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Mail size={14} /> Booking Email</label>
                <input
                  style={inputStyle}
                  value={settings.booking_email}
                  onChange={e => handleChange('booking_email', e.target.value)}
                  placeholder="booking@yourband.com"
                  disabled={!settings.show_booking_info}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Phone size={14} /> Booking Phone</label>
                <input
                  style={inputStyle}
                  value={settings.booking_phone}
                  onChange={e => handleChange('booking_phone', e.target.value)}
                  placeholder="+44 7700 000000"
                  disabled={!settings.show_booking_info}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Theme tab ── */}
      {activeTab === 'theme' && (
        <div>
          <p style={{ color: theme.textSecondary, marginTop: 0 }}>Choose a preset theme or set custom colours.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '2rem' }}>
            {THEMES.map(t => (
              <div
                key={t.id}
                onClick={() => applyTheme(t)}
                style={{
                  border: settings.theme_name === t.id ? `3px solid ${theme.primary}` : `2px solid ${theme.border}`,
                  borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
                  boxShadow: settings.theme_name === t.id ? `0 0 0 3px rgba(102,126,234,0.2)` : 'none',
                }}
              >
                <div style={{ display: 'flex', height: '48px' }}>
                  <div style={{ flex: 1, background: t.primary }} />
                  <div style={{ flex: 1, background: t.secondary }} />
                </div>
                <div style={{ padding: '0.5rem 0.6rem', background: t.background }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Custom colour pickers — always shown so user can fine-tune any theme */}
          <div style={{ background: theme.surfaceAlt, borderRadius: '10px', padding: '1.25rem' }}>
            <p style={{ margin: '0 0 1rem', fontWeight: '600', fontSize: '0.9rem', color: theme.text }}>
              {settings.theme_name === 'custom' ? 'Custom colours' : 'Fine-tune colours'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {[
                ['primary_color', 'Primary (buttons, header)'],
                ['secondary_color', 'Secondary (accents)'],
                ['background_color', 'Background'],
                ['text_color', 'Text'],
              ].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
                  <input
                    type="color"
                    value={settings[key]}
                    onChange={e => { handleChange(key, e.target.value); handleChange('theme_name', 'custom'); }}
                    style={{ width: '40px', height: '36px', padding: '2px', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.85rem', color: theme.textSecondary }}>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Preview strip */}
          <div style={{ marginTop: '1.5rem', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${theme.border}` }}>
            <div style={{ background: `linear-gradient(135deg, ${settings.primary_color} 0%, ${settings.secondary_color} 100%)`, padding: '0.75rem 1rem', color: 'white', fontSize: '0.85rem', fontWeight: '600' }}>
              Header preview
            </div>
            <div style={{ background: settings.background_color, padding: '0.75rem 1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span style={{ color: settings.text_color, fontSize: '0.9rem' }}>Song title · Artist</span>
              <button style={{ padding: '0.35rem 0.8rem', background: settings.primary_color, color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.8rem', cursor: 'default' }}>Request</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Social Links tab ── */}
      {activeTab === 'social' && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.show_social_media}
              onChange={e => handleChange('show_social_media', e.target.checked)}
              style={{ width: '18px', height: '18px' }}
            />
            <span style={{ fontWeight: '600' }}>Show social media links on audience page</span>
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', opacity: settings.show_social_media ? 1 : 0.4 }}>
            {SOCIAL_PLATFORMS.map(({ key, label, Icon, placeholder }) => (
              <div key={key}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Icon size={14} /> {label}</label>
                <input
                  style={inputStyle}
                  value={settings[key]}
                  onChange={e => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  disabled={!settings.show_social_media}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Band Members tab ── */}
      {activeTab === 'members' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Add / Edit form */}
          <div style={{ background: theme.surfaceAlt, borderRadius: '10px', padding: '1.25rem' }}>
            <p style={{ margin: '0 0 1rem', fontWeight: '700', fontSize: '0.85rem', color: theme.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {editingMemberId ? 'Edit Member' : 'Add Member'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={labelStyle}>Name *</label>
                <input style={inputStyle} value={memberForm.name} onChange={e => setMemberForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jamie Edwards" />
              </div>
              <div>
                <label style={labelStyle}>Role</label>
                <input style={inputStyle} value={memberForm.role} onChange={e => setMemberForm(f => ({ ...f, role: e.target.value }))} placeholder="e.g. Lead Guitar" />
              </div>
              <div>
                <label style={labelStyle}>Bio <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                <textarea
                  value={memberForm.bio}
                  onChange={e => setMemberForm(f => ({ ...f, bio: e.target.value }))}
                  rows={3}
                  placeholder="A short description..."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
              <div>
                <label style={labelStyle}>Photo <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                {memberForm.photo_url ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                    <img src={memberForm.photo_url} alt="Preview" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${theme.border}`, flexShrink: 0 }} />
                    <div>
                      <button type="button" onClick={() => memberPhotoInputRef.current.click()} style={{ display: 'block', marginBottom: '0.35rem', padding: '0.35rem 0.9rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
                        Change
                      </button>
                      <button type="button" onClick={() => setMemberForm(f => ({ ...f, photo_url: '' }))} style={{ display: 'block', padding: '0.35rem 0.9rem', background: theme.surface, color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => memberPhotoInputRef.current.click()}
                    style={{ border: `2px dashed ${theme.border}`, borderRadius: '8px', padding: '1.1rem', textAlign: 'center', cursor: 'pointer', color: theme.textMuted, background: theme.surfaceAlt, fontSize: '0.88rem' }}
                  >
                    <Camera size={14} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Upload photo
                  </div>
                )}
                <input ref={memberPhotoInputRef} type="file" accept="image/*" onChange={onSelectMemberPhoto} style={{ display: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={saveMember}
                  disabled={savingMember}
                  style={{
                    padding: '0.65rem 1.5rem',
                    background: savingMember ? '#aaa' : theme.navGradient,
                    color: theme.onPrimary, border: 'none', borderRadius: '8px',
                    fontWeight: '700', fontSize: '0.9rem', cursor: savingMember ? 'not-allowed' : 'pointer',
                  }}
                >
                  {savingMember ? 'Saving…' : editingMemberId ? 'Update Member' : 'Add Member'}
                </button>
                {editingMemberId && (
                  <button
                    onClick={() => { setMemberForm(emptyMember); setEditingMemberId(null); }}
                    style={{ padding: '0.65rem 1.25rem', background: theme.surface, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Member list */}
          {members.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1rem 0' }}>No band members added yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {members.map(m => (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                  padding: '0.85rem 1rem', background: theme.surface,
                  border: `1px solid ${theme.border}`, borderRadius: '10px',
                }}>
                  {m.photo_url ? (
                    <img src={m.photo_url} alt={m.name} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: theme.navGradient, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.onPrimary, fontWeight: '700', fontSize: '1.1rem', flexShrink: 0 }}>
                      {m.name[0].toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '700', fontSize: '0.95rem' }}>{m.name}</div>
                    {m.role && <div style={{ fontSize: '0.8rem', color: theme.primary, fontWeight: '600' }}>{m.role}</div>}
                    {m.bio && <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: theme.textSecondary, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{m.bio}</p>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    <button onClick={() => startEditMember(m)} style={{ padding: '0.3rem 0.7rem', background: theme.surfaceAlt, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: theme.text }}>Edit</button>
                    <button onClick={() => deleteMember(m.id)} style={{ padding: '0.3rem 0.7rem', background: theme.surface, border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#dc3545' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p style={{ margin: 0, fontSize: '0.8rem', color: theme.textMuted }}>
            Band members are shown on the <strong>Meet the Band</strong> page — accessible from your audience gig link.
          </p>
        </div>
      )}

      {/* ── Automation tab ── */}
      {activeTab === 'automation' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.auto_activate_enabled}
              onChange={e => handleChange('auto_activate_enabled', e.target.checked)}
              style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0 }}
            />
            <div>
              <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>Automatically manage active gig</span>
              <p style={{ margin: '0.3rem 0 0', fontSize: '0.82rem', color: theme.textMuted }}>
                When enabled, GigRequest will automatically activate the upcoming gig before it starts and deactivate it afterwards, based on the gig date and start time.
              </p>
            </div>
          </label>

          <div style={{ opacity: settings.auto_activate_enabled ? 1 : 0.4, pointerEvents: settings.auto_activate_enabled ? 'auto' : 'none', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            <div style={{ background: theme.surfaceAlt, borderRadius: '10px', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Activate gig this many minutes before start</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="number"
                    min="0"
                    max="480"
                    value={settings.auto_activate_mins_before}
                    onChange={e => handleChange('auto_activate_mins_before', parseInt(e.target.value) || 0)}
                    style={{ ...inputStyle, width: '100px' }}
                  />
                  <span style={{ fontSize: '0.85rem', color: theme.textMuted }}>
                    minutes {settings.auto_activate_mins_before >= 60 ? `(${(settings.auto_activate_mins_before / 60).toFixed(1).replace(/\.0$/, '')} hrs)` : ''}
                  </span>
                </div>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>e.g. 120 = activates 2 hours before the gig starts — useful for on-site sound checks</p>
              </div>

              <div>
                <label style={labelStyle}>Deactivate gig this many minutes after start</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="number"
                    min="0"
                    max="720"
                    value={settings.auto_deactivate_mins_after}
                    onChange={e => handleChange('auto_deactivate_mins_after', parseInt(e.target.value) || 0)}
                    style={{ ...inputStyle, width: '100px' }}
                  />
                  <span style={{ fontSize: '0.85rem', color: theme.textMuted }}>
                    minutes {settings.auto_deactivate_mins_after >= 60 ? `(${(settings.auto_deactivate_mins_after / 60).toFixed(1).replace(/\.0$/, '')} hrs)` : ''}
                  </span>
                </div>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>e.g. 180 = deactivates 3 hours after start, giving time for a full 2-set gig to finish</p>
              </div>
            </div>

            <div style={{ background: theme.isDark ? '#1a2a1a' : '#f0f7f0', border: `1px solid ${theme.isDark ? '#2a4a2a' : '#c3e6cb'}`, borderRadius: '8px', padding: '0.85rem 1rem', fontSize: '0.85rem', color: theme.isDark ? '#7ec87e' : '#2d5a2d' }}>
              Gigs must have both a <strong>date</strong> and <strong>start time</strong> set to be auto-managed. You can still manually set any gig active at any time.
            </div>
          </div>
        </div>
      )}

      {/* ── Public Page tab ── */}
      {activeTab === 'publicpage' && (() => {
        const isPro = settings.subscription_tier === 'pro' || settings.subscription_tier === 'band';
        const layouts = [
          {
            id: 'hero',
            label: 'Hero',
            desc: 'Full-screen photo slideshow with band name overlaid. Simple and impactful.',
            locked: false,
            preview: (
              <div style={{ borderRadius: 6, overflow: 'hidden', height: 90, background: '#1a1a2e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                <div style={{ width: '40%', height: 6, background: 'rgba(255,255,255,0.7)', borderRadius: 3 }} />
                <div style={{ width: '25%', height: 4, background: 'rgba(255,255,255,0.4)', borderRadius: 3 }} />
                <div style={{ position: 'absolute', bottom: 8, display: 'flex', gap: 5 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />)}
                </div>
              </div>
            ),
          },
          {
            id: 'presskit',
            label: 'Press Kit',
            desc: 'Professional two-column layout. Great for sharing with booking agents.',
            locked: !isPro,
            preview: (
              <div style={{ borderRadius: 6, overflow: 'hidden', height: 90, background: theme.surfaceAlt, display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: 22, background: `linear-gradient(90deg, ${theme.primary}, #764ba2)` }} />
                <div style={{ flex: 1, display: 'flex', gap: 6, padding: '6px 8px' }}>
                  <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {[60,45,45].map((w,i) => <div key={i} style={{ height: 5, width: `${w}%`, background: theme.border, borderRadius: 3 }} />)}
                  </div>
                  <div style={{ flex: 3, background: theme.border, borderRadius: 4 }} />
                </div>
              </div>
            ),
          },
          {
            id: 'showcase',
            label: 'Showcase',
            desc: 'Full scroll landing page with hero, about, shows and connect sections.',
            locked: !isPro,
            preview: (
              <div style={{ borderRadius: 6, overflow: 'hidden', height: 90, background: theme.surfaceAlt, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ height: 40, background: '#1a1a2e', display: 'flex', alignItems: 'flex-end', padding: '0 8px 6px' }}>
                  <div style={{ width: '35%', height: 5, background: 'rgba(255,255,255,0.6)', borderRadius: 3 }} />
                </div>
                {[0,1,2].map(i => (
                  <div key={i} style={{ flex: 1, background: i % 2 === 0 ? theme.surface : theme.surfaceAlt, padding: '3px 8px', display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: `${[55,70,50][i]}%`, height: 4, background: theme.border, borderRadius: 3 }} />
                  </div>
                ))}
              </div>
            ),
          },
        ];

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label style={labelStyle}>Page Layout</label>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: theme.textMuted }}>
                Choose how your public band page at <code style={{ background: theme.surfaceAlt, padding: '1px 6px', borderRadius: 4 }}>/band/{settings.band_slug || 'your-slug'}</code> is displayed.
                {!isPro && <span style={{ color: '#f59e0b', marginLeft: 6 }}>Upgrade to Pro to unlock all layouts.</span>}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                {layouts.map(({ id, label, desc, locked, preview }) => {
                  const selected = settings.public_page_layout === id;
                  return (
                    <div key={id}
                      onClick={() => {
                        if (locked) { toast.error('Upgrade to Pro to unlock this layout'); return; }
                        handleChange('public_page_layout', id);
                      }}
                      style={{
                        border: selected ? `2px solid ${theme.primary}` : `2px solid ${theme.border}`,
                        borderRadius: 10, padding: 12, cursor: locked ? 'default' : 'pointer',
                        background: selected ? `${theme.primary}10` : theme.surface,
                        opacity: locked ? 0.65 : 1, position: 'relative', transition: 'border-color 0.15s',
                      }}
                    >
                      <div style={{ position: 'relative', marginBottom: 10 }}>
                        {preview}
                        {locked && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', borderRadius: 6 }}>
                            <span style={{ fontSize: 18 }}>🔒</span>
                          </div>
                        )}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: theme.text, marginBottom: 4 }}>
                        {label} {locked && <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 500 }}>Pro</span>}
                      </div>
                      <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4 }}>{desc}</div>
                      {selected && (
                        <div style={{ position: 'absolute', top: 8, right: 8, width: 18, height: 18, borderRadius: '50%', background: theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Public page URL</label>
              {settings.band_slug ? (
                <a href={`/band/${settings.band_slug}`} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 14, color: theme.primary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  /band/{settings.band_slug}
                  <span style={{ fontSize: 11 }}>↗</span>
                </a>
              ) : (
                <p style={{ margin: 0, fontSize: 14, color: theme.textMuted }}>Set your Audience URL Slug in Band Info to enable your public page.</p>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Tech Rider tab ── */}
      {activeTab === 'techrider' && (() => {
        const rider = getRider(activeRiderProfile);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Profile switcher */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[
                { key: 'full',    label: 'Full Setup' },
                { key: 'minimal', label: 'Minimal Setup' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveRiderProfile(key)}
                  style={{
                    padding: '0.45rem 1rem', borderRadius: '20px', cursor: 'pointer',
                    border: activeRiderProfile === key ? 'none' : `1px solid ${theme.border}`,
                    background: activeRiderProfile === key ? theme.primary : 'transparent',
                    color: activeRiderProfile === key ? theme.onPrimary : theme.textSecondary,
                    fontWeight: activeRiderProfile === key ? '600' : '400',
                    fontSize: '0.875rem', transition: 'background 0.15s, color 0.15s',
                  }}
                >{label}</button>
              ))}
            </div>

            {/* Profile title */}
            <div>
              <label style={labelStyle}>Profile Name</label>
              <input
                type="text"
                value={rider.title}
                onChange={e => setRider(activeRiderProfile, { title: e.target.value })}
                style={inputStyle}
              />
            </div>

            {/* Input list */}
            <div>
              <label style={labelStyle}>Input List</label>
              {rider.inputs.length > 0 && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 90px 36px', gap: '0.4rem', marginBottom: '0.35rem' }}>
                    {['Ch', 'Name', 'Source', ''].map((h, i) => (
                      <span key={i} style={{ fontSize: '0.75rem', fontWeight: '600', color: theme.textMuted, paddingLeft: i === 0 ? '0.4rem' : 0 }}>{h}</span>
                    ))}
                  </div>
                  {rider.inputs.map((row, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 90px 36px', gap: '0.4rem', marginBottom: '0.35rem', alignItems: 'center' }}>
                      <input
                        type="number"
                        min="1"
                        value={row.ch}
                        onChange={e => updateRiderInput(activeRiderProfile, idx, 'ch', parseInt(e.target.value) || idx + 1)}
                        style={{ ...inputStyle, padding: '0.4rem', textAlign: 'center', fontSize: '0.85rem' }}
                      />
                      <input
                        type="text"
                        value={row.name}
                        placeholder="e.g. Lead Vocal"
                        onChange={e => updateRiderInput(activeRiderProfile, idx, 'name', e.target.value)}
                        style={{ ...inputStyle, padding: '0.4rem', fontSize: '0.85rem' }}
                      />
                      <select
                        value={row.source}
                        onChange={e => updateRiderInput(activeRiderProfile, idx, 'source', e.target.value)}
                        style={{ ...inputStyle, padding: '0.4rem', fontSize: '0.85rem', cursor: 'pointer' }}
                      >
                        <option value="mic">Mic</option>
                        <option value="di">DI</option>
                        <option value="direct">Direct</option>
                      </select>
                      <button
                        onClick={() => removeRiderInput(activeRiderProfile, idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '1.1rem', lineHeight: 1, padding: '0.3rem' }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => addRiderInput(activeRiderProfile)}
                style={{ padding: '0.4rem 0.9rem', background: 'none', border: `1px dashed ${theme.border}`, borderRadius: '6px', cursor: 'pointer', color: theme.textSecondary, fontSize: '0.85rem' }}
              >+ Add Input</button>
            </div>

            {/* PA Required */}
            <div>
              <label style={labelStyle}>PA Required from Venue</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[{ val: true, label: 'Yes' }, { val: false, label: 'No — band brings own' }].map(({ val, label }) => (
                  <button
                    key={String(val)}
                    onClick={() => setRider(activeRiderProfile, { pa_required: val })}
                    style={{
                      padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer',
                      border: `1px solid ${rider.pa_required === val ? theme.primary : theme.border}`,
                      background: rider.pa_required === val ? `${theme.primary}18` : theme.surface,
                      color: rider.pa_required === val ? theme.primary : theme.textSecondary,
                      fontWeight: rider.pa_required === val ? '600' : '400',
                      fontSize: '0.875rem',
                    }}
                  >{label}</button>
                ))}
              </div>
            </div>

            {/* Monitors */}
            <div>
              <label style={labelStyle}>Monitor Requirements</label>
              <textarea
                value={rider.monitors}
                onChange={e => setRider(activeRiderProfile, { monitors: e.target.value })}
                placeholder="e.g. 2 mixes — lead vocal + guitar amp. IEM preferred for drummer."
                rows={3}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            {/* Backline */}
            <div>
              <label style={labelStyle}>Backline Notes</label>
              <textarea
                value={rider.backline}
                onChange={e => setRider(activeRiderProfile, { backline: e.target.value })}
                placeholder="e.g. Band brings own guitar/bass amps. Drum kit and stool required from venue."
                rows={3}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            {/* Notes */}
            <div>
              <label style={labelStyle}>Notes for Sound Engineer</label>
              <textarea
                value={rider.notes}
                onChange={e => setRider(activeRiderProfile, { notes: e.target.value })}
                placeholder="e.g. Gates on kick and snare please. Reverb on lead vocal."
                rows={3}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            {/* Save */}
            <div style={{ paddingTop: '0.5rem' }}>
              <button
                onClick={() => saveRider(activeRiderProfile)}
                disabled={savingRider}
                style={{
                  padding: '0.85rem 2.5rem',
                  background: savingRider ? '#aaa' : theme.navGradient,
                  color: theme.onPrimary, border: 'none', borderRadius: '10px',
                  fontSize: '1rem', fontWeight: '700',
                  cursor: savingRider ? 'not-allowed' : 'pointer',
                }}
              >
                {savingRider ? 'Saving…' : `Save ${rider.title}`}
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Logins & Access tab (owners only) ── */}
      {activeTab === 'logins' && bandRole === 'owner' && (
        <LoginsAccess theme={theme} bandName={settings.band_name} />
      )}

      {/* Save button — not shown on Band Members, Tech Rider or Logins tabs (they save independently) */}
      {activeTab !== 'members' && activeTab !== 'techrider' && activeTab !== 'logins' && (
        <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: `1px solid ${theme.border}` }}>
          <button
            onClick={save}
            disabled={saving}
            style={{
              padding: '0.85rem 2.5rem', background: saving ? '#aaa' : theme.navGradient,
              color: theme.onPrimary, border: 'none', borderRadius: '10px',
              fontSize: '1rem', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      )}

      {/* Photo crop modal */}
      {cropSrc && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            background: theme.surface, borderRadius: '14px', padding: '1.5rem',
            width: '100%', maxWidth: '420px',
            display: 'flex', flexDirection: 'column', gap: '1rem',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Crop photo</h3>
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>Drag to reposition · resize handles to zoom</p>
              </div>
              <button onClick={cancelCrop} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1, padding: '0.2rem 0.4rem' }}>×</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', overflow: 'hidden', borderRadius: '8px', background: '#111' }}>
              <ReactCrop
                crop={crop}
                onChange={c => setCrop(c)}
                onComplete={c => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  src={cropSrc}
                  onLoad={onImageLoad}
                  style={{ maxWidth: '100%', maxHeight: '55vh', display: 'block' }}
                  alt="Crop preview"
                />
              </ReactCrop>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={confirmCrop}
                style={{
                  flex: 1, padding: '0.75rem',
                  background: theme.navGradient,
                  color: theme.onPrimary, border: 'none', borderRadius: '8px',
                  fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem',
                }}
              >
                Use this photo
              </button>
              <button
                onClick={cancelCrop}
                style={{ padding: '0.75rem 1.1rem', background: theme.surface, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default BrandingSettings;
